import React, { useState, lazy, Suspense } from "react";
import "./realTimeAssist.scss";
import StepsAccordion from "../StepsAccordian/StepsAccordion";
import { AccordionDetails, AccordionSummary, Tooltip } from "@mui/material";
import { ReactComponent as ExpandMoreIcon } from "../../assets/icons/ExpandMore.svg";
import ChooseFile from "../ChooseFile/ChooseFile";
import Checkboxes from "../Checkboxes/Checkboxes";
import { REALTIME_ASSIST_COMPONENTS } from "../../data/realTimeAssistComponents";
import { ReactComponent as X } from "../../assets/icons/X.svg";
import { CONVERSATION_TYPES, SAMPLE_FILES, DEFAULT_SCRIPT_SCENARIOS } from "../../constants";
import ShowScript from "./ShowScript/ShowScript";
import {generateSampleScript} from "../../services/realTimeAssist";
import {RotatingLines} from "react-loader-spinner";

const QuestionHandling = lazy(() => import("./QuestionHandling/QuestionHandling"));
const ObjectionHandling = lazy(() => import("./ObjectionHandling/ObjectionHandling"));
const ComplianceHandling = lazy(() => import("./ComplianceHandling/complianceHandling"));
const ScriptAdherence = lazy(() => import("./ScriptAdherence/scriptAdherence"));
const CommunicationStyle = lazy(() => import("./CommunicationStyle/communicationStyle"));

const COMPONENT_MAPPING = {
  '1': ObjectionHandling,
  '2': ScriptAdherence,
  '3': QuestionHandling,
  '4': ComplianceHandling,
  '5': CommunicationStyle
};

const RealTimeAssist = ({ setFinalConversationId }) => {
  const [interactionType, setInteractionType] = useState(null);
  const [sampleFileSelected, setSampleFileSelected] = useState(null);
  const [componentsSelectedIds, setComponentsSelectedIds] = useState([]);
  const [showScriptSelection, setShowScriptSelection] = useState(false);
  const [webSocketData, setWebSocketData] = useState(null);
  const [linkCopiedHidden, setLinkCopiedHidden] = useState(true);
  const [expandedStep1, setExpandedStep1] = useState(true);
  const [expandedStep2, setExpandedStep2] = useState(false);
  const [expandedStep3, setExpandedStep3] = useState(false);
  const [expandedStep4, setExpandedStep4] = useState(false);
  const [websiteLink, setWebsiteLink] = useState("");
  const [scriptSummary, setScriptSummary] = useState({});

  const updateStateFromLocation = (params) => {
    const callParam = params.get('call');
    const assistantSelected = params.get('assistant');
    const showScript = params.get('showscript');
    let website = params.get('website');

    if (callParam !== null) {
      handleSampleFileSelected(callParam);
      console.log(`Call updated to ${callParam}`);
    }
    if (assistantSelected != null) {
      let assistantSelectedStr = assistantSelected.split(",");
      setComponentsSelectedIds(assistantSelectedStr);
      console.log(`Assistant updated to ${assistantSelectedStr}`);

      setInteractionType("HUMAN_TO_HUMAN");
      console.log(`Interaction Type Updated to ${interactionType}`);

    }

    if (showScript != null) {
      setShowScriptSelection(showScript === 'true');
      console.log(`Show script updated to ${showScript}`);
    }

    if(website != null) {
      setWebsiteLink(website);
      console.log(`Updated website to ${website}`);
    }

    if(callParam && website) {
      website = decodeURIComponent(website)
      handleWebsiteLinkBlur(null, callParam, website).then(() => {console.log("Script updated.")});
    }

  };

  const handleSampleFileSelected = (file) => {
    console.log("Sample file selected:", file);
    setSampleFileSelected(file);
    setScriptSummary(DEFAULT_SCRIPT_SCENARIOS[file].summary)
  };

  const handleRealTimeAssistComponentSelection = (name, checked) => {
    console.log(`Selected ${name} checked ${checked}`);
    const index = componentsSelectedIds.indexOf(name);
    if (checked && index === -1) {
      setComponentsSelectedIds((scoreCards) => [...scoreCards, name]);
    } else if (!checked && index !== -1) {
      setComponentsSelectedIds((scoreCards) => {
        const clone = [...scoreCards];
        clone.splice(index, 1);
        return clone;
      });
    }
  };

  const handleViewScriptButtonClick = () => {
    console.log(`View Script clicked, update Script for ${sampleFileSelected} website ${websiteLink}`);
    handleWebsiteLinkBlur(null, null, null, () => setShowScriptSelection(true));
  };

  const handleCloseLinkCopied = () => {
    setLinkCopiedHidden(true);
    console.log("Link copied hidden");
  };

  const disableNextButton = !sampleFileSelected || componentsSelectedIds.length === 0 || !interactionType;

  const handleDataReceived = (data) => {
    setWebSocketData(data);
    console.log("WebSocket data received:", data);
  };

  const handleInteractionTypeSelected = (name) => {
    setInteractionType(name);
    console.log("Interaction type selected:", name);
  };

  const handleChangeStep1 = () => {
    setExpandedStep1(prevExpanded => !prevExpanded);
    console.log("Step 1 expanded:", !expandedStep1);
  };

  const handleChangeStep2 = () => {
    setExpandedStep2(prevExpanded => !prevExpanded);
    console.log("Step 2 expanded:", !expandedStep2);
  };

  const handleChangeStep3 = () => {
    setExpandedStep3(prevExpanded => !prevExpanded);
    console.log("Step 3 expanded:", !expandedStep3);
  };

  const handleChangeStep4 = () => {
    setExpandedStep4(prevExpanded => !prevExpanded);
    console.log("Step 4 expanded:", !expandedStep4);
  };

  const handleStep1Next = () => {
    setExpandedStep1(false);
    setExpandedStep2(true);
    console.log("Step 1 next clicked");
  };

  const handleStep2Next = () => {
    setExpandedStep2(false);
    setExpandedStep3(true);
    console.log("Step 2 next clicked");
  };

  const handleStep3Next = () => {
    setExpandedStep3(false);
    setExpandedStep4(true);
    console.log("Step 3 next clicked");
  };

  const handleStep4Next = () => {
    // Implement next step logic for Step 4
    console.log("Step 4 next clicked");
  };

  const handleWebsiteLinkChange = (event) => {
    setWebsiteLink(event.target.value);
  };

  const handleWebsiteLinkBlur = async(event, callType, website, cb) => {

    website = website || websiteLink;
    callType = SAMPLE_FILES[callType || sampleFileSelected].name

    if (website) {
      setScriptSummary({"scenario": "", "features": "", "use_cases": ""});
      generateSampleScript(callType, website).then(sampleScript => {
        if (sampleScript) {
          setScriptSummary(sampleScript);
        } else {
          console.error("Failed to generate script. Using default scenario.");
          setScriptSummary(DEFAULT_SCRIPT_SCENARIOS[sampleFileSelected].summary);
        }
        if(cb) cb();
      });
    } else {
      console.log("No website provided. Using default scenario.", sampleFileSelected);
      console.log(DEFAULT_SCRIPT_SCENARIOS[sampleFileSelected]?.summary)
      setScriptSummary(DEFAULT_SCRIPT_SCENARIOS[sampleFileSelected]?.summary);
      if(cb) cb();
    }
  };

  return (
    <div className={"real-time-assist-container"}>
      <div className={showScriptSelection ? "hide-customer-call" : "real-time-assist-options"}>
        <div>
          <StepsAccordion expanded={expandedStep1} onChange={handleChangeStep1}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="choose-criteria-or-score card" id="step1-header">
              <p>
                <b> Step 1</b>
              </p>
            </AccordionSummary>
            <AccordionDetails>
              <ChooseFile onSampleFileSelected={handleSampleFileSelected} selectedSampleFileName={sampleFileSelected} showOptionToChooseCustomFile={false} label="Choose a call type."/>
              <button className='next-step-button'
                      disabled={!sampleFileSelected}
                      title={`${!sampleFileSelected ? 'Please select a file to move to the next step' : ''}`}
                      onClick={handleStep1Next}>
                <p>Next Step</p>
              </button>
            </AccordionDetails>
          </StepsAccordion>
          <div className='divider'/>
          <StepsAccordion expanded={expandedStep2} onChange={handleChangeStep2}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} id="step2-header">
              <p>
                <b> Step 2 </b>
              </p>
            </AccordionSummary>
            <AccordionDetails>
              <Checkboxes optionsAvailable={REALTIME_ASSIST_COMPONENTS}
                          onCheckboxClick={handleRealTimeAssistComponentSelection}
                          selectedIds={componentsSelectedIds}
                          maxSelections={2}
                          label="Choose assist components. Select up to 2."
                          showModal={true} modalTitle={"Assistant"}
              />
              <button className='next-step-button'
                      disabled={componentsSelectedIds.length === 0}
                      title={`${componentsSelectedIds.length === 0 ? 'Please select at least one assist component to move to the next step' : ''}`}
                      onClick={handleStep2Next}>
                <p>Next Step</p>
              </button>
            </AccordionDetails>
          </StepsAccordion>
          <div className='divider'/>
          <StepsAccordion expanded={expandedStep3} onChange={handleChangeStep3}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} id="step3-header">
              <p>
                <b> Step 3 </b>
              </p>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <p>Provide context (optional):</p>
                <Tooltip title="Provide the website link to the product you are selling or you are supporting as an agent." placement="top">
                  <div className="website-link-container">
                    <X className="clear-icon" onClick={() => setWebsiteLink("")} />
                    <input type="text" value={websiteLink} onChange={handleWebsiteLinkChange}  placeholder="Your website link" />
                  </div>
                </Tooltip>
              </div>
              <button className='next-step-button'
                      onClick={handleStep3Next}>
                <p>Next Step</p>
              </button>
            </AccordionDetails>
          </StepsAccordion>
          <div className='divider'/>
          <StepsAccordion expanded={expandedStep4} onChange={handleChangeStep4}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="choose-interaction-type" id="step4-header">
              <p>
                <b> Step 4</b>
              </p>
            </AccordionSummary>
            <AccordionDetails>
              <ChooseFile onSampleFileSelected={handleInteractionTypeSelected} selectedSampleFileName={interactionType} showOptionToChooseCustomFile={false} label="Choose how you want to test." options={CONVERSATION_TYPES} />
            </AccordionDetails>

          </StepsAccordion>
        </div>
        <div className={"view-script-button"}>
          <button className={"blue-button"} disabled={disableNextButton || scriptSummary.scenario === ""} title={`${disableNextButton ? "Please choose real-time assist components and sample script." : ""}`} onClick={handleViewScriptButtonClick}>
            <p>{scriptSummary.scenario === "" ? <RotatingLines strokeColor="white" strokeWidth="2" width="22" /> : "View Script"}</p>
          </button>
        </div>
      </div>
      <Suspense fallback={<div>Loading...</div>}>
          <ShowScript
            className={showScriptSelection ? "real-time-assist-options" : "hide-customer-call"}
            showScriptFn={setShowScriptSelection}
            hideLinkCopiedFn={setLinkCopiedHidden}
            scriptTitle={sampleFileSelected ? SAMPLE_FILES[sampleFileSelected].name : ''}
            onDataReceived={handleDataReceived}
            interactionType={interactionType}
            websiteLink={websiteLink}
            scriptSummary={scriptSummary}
            propData={{ sampleFileSelected, componentsSelectedIds }}
            updateStateFromLocation={updateStateFromLocation}
            setFinalConversationId={setFinalConversationId}
          />
      </Suspense>
      {!showScriptSelection && (
          <div className={"column-2 selection-stage"}>
            <p className={"title"}>Real time assist</p>
            <p className={"sub-text"}>Please select call, assist component to see real time assist</p>
          </div>
      )}
      {showScriptSelection && (
        <>
          <div className={"column-2"}>
            {componentsSelectedIds && componentsSelectedIds.slice(0, 1).map((component) => {
              const Component = COMPONENT_MAPPING[component];
              return Component ? (
                <Suspense fallback={<div>Loading...</div>} key={component}>
                  <Component data={webSocketData} callType={SAMPLE_FILES[sampleFileSelected]} />
                </Suspense>
              ) : null;
            })}
          </div>
          <div className={"column-3"}>
            {componentsSelectedIds && componentsSelectedIds.slice(1, 2).map((component) => {
              const Component = COMPONENT_MAPPING[component];
              return Component ? (
                <Suspense fallback={<div>Loading...</div>} key={component}>
                  <Component data={webSocketData} callType={SAMPLE_FILES[sampleFileSelected]} showScriptSection={showScriptSelection}/>
                </Suspense>
              ) : null;
            })}
          </div>
        </>
      )}
      {!linkCopiedHidden && (
        <div className="link-copied">
          <div className="link-copied-container">
            <p className="link-copied-text">Link Copied</p>
            <X className="link-copied-close-icon" onClick={handleCloseLinkCopied} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RealTimeAssist;
